import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    View, FlatList, StyleSheet, RefreshControl,
    Text, TouchableOpacity, Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getFeed, toggleLike, repostPost } from '../../../api/social';
import PostCard from '../../../components/social/feed/PostCard';
import { FeedSkeleton, SmoothFeedLoader } from '../../../components/shared/SkeletonLoader';

const COMPOSE_BAR_H = 58; // height of the thin compose strip we now embed as list header

const FeedScreen = ({ navigation, scrollY: sharedScrollY }) => {
    const queryClient = useQueryClient();
    const insets = useSafeAreaInsets();
    const [page, setPage] = useState(1);
    const [allPosts, setAllPosts] = useState([]);
    const [viewableItemIds, setViewableItemIds] = useState([]);

    // ── Scroll-based FAB label hide ──────────────────────────────────────
    const lastScrollY = useRef(0);
    const fabLabelVisible = useRef(new Animated.Value(1)).current; // 1 = show label
    const fabLabelWidth = useRef(new Animated.Value(80)).current;  // label width

    const handleScroll = Animated.event(
        [{ nativeEvent: { contentOffset: { y: sharedScrollY } } }],
        {
            useNativeDriver: false,
            listener: (event) => {
                const y = event.nativeEvent.contentOffset.y;
                const goingDown = y > lastScrollY.current && y > 30;
                const goingUp = y < lastScrollY.current;
                lastScrollY.current = y;

                if (goingDown) {
                    Animated.parallel([
                        Animated.timing(fabLabelVisible, { toValue: 0, duration: 180, useNativeDriver: false }),
                        Animated.timing(fabLabelWidth, { toValue: 0, duration: 180, useNativeDriver: false }),
                    ]).start();
                } else if (goingUp) {
                    Animated.parallel([
                        Animated.timing(fabLabelVisible, { toValue: 1, duration: 180, useNativeDriver: false }),
                        Animated.timing(fabLabelWidth, { toValue: 80, duration: 180, useNativeDriver: false }),
                    ]).start();
                }
            }
        }
    );

    // ── Data fetching ────────────────────────────────────────────────────
    const { data, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['feed', page],
        queryFn: () => getFeed(page),
        keepPreviousData: true,
    });

    useEffect(() => {
        if (data?.posts) {
            if (page === 1) {
                setAllPosts(data.posts);
            } else {
                setAllPosts(prev => {
                    const existingIds = new Set(prev.map(p => p.id));
                    const unique = data.posts.filter(p => !existingIds.has(p.id));
                    return [...prev, ...unique];
                });
            }
        }
    }, [data, page]);

    // ── Exposed refresh helper (called by CreatePostScreen via navigation) ─
    const handlePostCreated = useCallback(async () => {
        setPage(1);
        const fresh = await queryClient.fetchQuery({
            queryKey: ['feed', 1],
            queryFn: () => getFeed(1),
        });
        if (fresh?.posts) setAllPosts(fresh.posts);
    }, [queryClient]);

    // Pass the refresh fn to CreatePostScreen via navigation params
    useEffect(() => {
        navigation.setParams?.({ onPostCreated: handlePostCreated });
    }, [handlePostCreated, navigation]);

    const handleRefresh = useCallback(async () => {
        setPage(1);
        await refetch();
    }, [refetch]);

    const handleLoadMore = useCallback(() => {
        if (data && page < data.totalPages) {
            setPage(prev => prev + 1);
        }
    }, [data, page]);

    // ── Mutations ─────────────────────────────────────────────────────────
    const likeMutation = useMutation({
        mutationFn: toggleLike,
        onMutate: async (postId) => {
            await queryClient.cancelQueries({ queryKey: ['feed'] });
            const previousPosts = allPosts;
            setAllPosts(prev => prev.map(post =>
                post.id === postId
                    ? { ...post, isLiked: !post.isLiked, likesCount: post.isLiked ? post.likesCount - 1 : post.likesCount + 1 }
                    : post
            ));
            return { previousPosts };
        },
        onError: (_err, _postId, context) => {
            if (context?.previousPosts) setAllPosts(context.previousPosts);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['feed'] });
        },
    });

    const repostMutation = useMutation({
        mutationFn: repostPost,
        onMutate: async (postId) => {
            await queryClient.cancelQueries({ queryKey: ['feed'] });
            const previousPosts = allPosts;
            setAllPosts(prev => {
                if (!prev) return prev;
                return prev.map(post =>
                    post.id === postId
                        ? { ...post, repostsCount: (post.repostsCount || 0) + 1 }
                        : post
                );
            });
            return { previousPosts };
        },
        onError: (_err, _postId, context) => {
            if (context?.previousPosts) setAllPosts(context.previousPosts);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['feed'] });
            // Don't reset page here for optimistic feel, just let it update in bg
        },
    });

    const handlePostDeleted = useCallback((postId) => {
        setAllPosts(prev => prev.filter(p => p.id !== postId));
        queryClient.invalidateQueries(['feed']);
    }, [queryClient]);

    // Derive the posts to display to avoid the one-frame flash when allPosts hasn't updated yet
    const displayPosts = (page === 1 && data?.posts) ? data.posts : allPosts;

    // ── Viewability Config (Auto-play/pause) ─────────────────────────────
    const viewabilityConfig = useRef({
        itemVisiblePercentThreshold: 50, // Item is "visible" if >50% is on screen
    }).current;

    const onViewableItemsChanged = useRef(({ viewableItems }) => {
        const ids = viewableItems.map(item => item.key);
        setViewableItemIds(ids);
    }).current;

    // ── Render helpers ────────────────────────────────────────────────────
    const renderItem = useCallback(({ item }) => {
        const isLiked = item.isLiked !== undefined ? item.isLiked : (item.likes?.length > 0);
        const isVisible = viewableItemIds.includes(item.id.toString());

        return (
            <PostCard
                post={item}
                isLiked={isLiked}
                isVisible={isVisible}
                onLike={() => likeMutation.mutate(item.id)}
                onComment={() => navigation.navigate('PostDetail', { postId: item.id })}
                onProfilePress={(userId) => navigation.navigate('UserProfile', { userId })}
                onShare={() => repostMutation.mutate(item.id)}
                onPostDeleted={handlePostDeleted}
            />
        );
    }, [likeMutation, repostMutation, navigation, handlePostDeleted, viewableItemIds]);

    const renderFooter = () => {
        if (isLoading && page > 1) {
            return <SmoothFeedLoader text="Loading more..." />;
        }
        return <View style={{ height: 100 }} />;
    };

    const navigateToCreate = useCallback(() => {
        navigation.navigate('CreatePost', { onPostCreated: handlePostCreated });
    }, [navigation, handlePostCreated]);

    return (
        <View style={styles.container}>
            <FlatList
                data={displayPosts}
                renderItem={renderItem}
                keyExtractor={item => item.id.toString()}
                ListHeaderComponent={<View style={{ height: 44 }} />}
                ListFooterComponent={renderFooter}
                contentContainerStyle={styles.listContent}
                onScroll={handleScroll}
                scrollEventThrottle={16}
                viewabilityConfig={viewabilityConfig}
                onViewableItemsChanged={onViewableItemsChanged}
                refreshControl={
                    <RefreshControl
                        refreshing={isLoading && page === 1}
                        onRefresh={handleRefresh}
                        tintColor="#06b6d4"
                        colors={['#06b6d4']}
                    />
                }
                onEndReached={handleLoadMore}
                onEndReachedThreshold={0.5}
                showsVerticalScrollIndicator={false}
                ListEmptyComponent={() => {
                    if (isLoading || (isFetching && displayPosts.length === 0)) {
                        return <FeedSkeleton count={4} />;
                    }
                    if (displayPosts.length === 0) {
                        return <SmoothFeedLoader text="No posts here yet — stay tuned!" />;
                    }
                    return null;
                }}
            />

            {/* ── X-style Circular FAB ── */}
            <TouchableOpacity
                style={[styles.fab, { bottom: insets.bottom + 20 }]}
                onPress={navigateToCreate}
                activeOpacity={0.85}
            >
                <Ionicons name="add" size={30} color="#FFFFFF" />
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    listContent: {
        paddingBottom: 8,
    },


    // Circular FAB
    fab: {
        position: 'absolute',
        right: 18,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: '#1D9BF0',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#1D9BF0',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.35,
        shadowRadius: 10,
        elevation: 8,
    },
});

export default FeedScreen;
